/**
 * Container Traversal - Pure Utility Functions
 *
 * Functions for traversing the nested container structure.
 * Used for finding parents, calculating nesting depth, and detecting cycles.
 *
 * Performance: O(n) where n = total containers in canvas (including nested)
 */

import type { ContainerAPI } from '../../types/api.types';
import type { NestingInfo } from '../types/container-row.types';
import { CONTAINER_CONSTRAINTS } from '../constants/constraints';

// ============================================================================
// Nesting Depth Functions
// ============================================================================

/**
 * Gets the nesting depth of a container
 *
 * @param containers - Top-level containers in the canvas
 * @param containerId - ID of the container to find depth for
 * @returns Nesting depth (0 = root, 1 = nested under root, etc.) or -1 if not found
 *
 * Time: O(n), Space: O(d) where d = max nesting depth
 */
export const getNestingDepth = (
  containers: ContainerAPI[],
  containerId: string
): number => {
  // Check if it's a top-level container (depth 0)
  if (containers.some((c) => c.id === containerId)) {
    return 0;
  }

  // Search recursively through nested containers
  for (const container of containers) {
    const depth = findDepthRecursive(container, containerId, 1);
    if (depth >= 0) {
      return depth;
    }
  }

  return -1; // Not found
};

/**
 * Helper: Recursively find container depth
 */
const findDepthRecursive = (
  parent: ContainerAPI,
  targetId: string,
  currentDepth: number
): number => {
  const nested = parent.content?.nestedContainers || [];

  for (const child of nested) {
    if (child.id === targetId) {
      return currentDepth;
    }

    const depth = findDepthRecursive(child, targetId, currentDepth + 1);
    if (depth >= 0) {
      return depth;
    }
  }

  return -1;
};

/**
 * Gets complete nesting information for a container
 *
 * @param containers - Top-level containers in the canvas
 * @param containerId - ID of the container
 * @returns Nesting information
 *
 * Time: O(n), Space: O(d)
 */
export const getNestingInfo = (
  containers: ContainerAPI[],
  containerId: string
): NestingInfo => {
  const depth = getNestingDepth(containers, containerId);
  const parentId = findParentContainerId(containers, containerId);

  return {
    depth: depth === -1 ? 0 : depth,
    isRoot: depth === 0,
    isNested: depth > 0,
    parentId,
    canNest: depth < CONTAINER_CONSTRAINTS.MAX_NESTING_DEPTH - 1,
  };
};

// ============================================================================
// Parent Finding Functions
// ============================================================================

/**
 * Finds the parent container of a nested container
 *
 * @param containers - Top-level containers in the canvas
 * @param childId - ID of the child container to find parent for
 * @returns Parent container or null if not found or if child is top-level
 *
 * Time: O(n), Space: O(d)
 */
export const findParentContainer = (
  containers: ContainerAPI[],
  childId: string
): ContainerAPI | null => {
  // Top-level containers have no parent
  if (containers.some((c) => c.id === childId)) {
    return null;
  }

  // Search recursively
  for (const container of containers) {
    const parent = findParentRecursive(container, childId);
    if (parent) {
      return parent;
    }
  }

  return null;
};

/**
 * Finds the parent container ID (lightweight version)
 *
 * @param containers - Top-level containers in the canvas
 * @param childId - ID of the child container
 * @returns Parent container ID or null
 */
export const findParentContainerId = (
  containers: ContainerAPI[],
  childId: string
): string | null => {
  const parent = findParentContainer(containers, childId);
  return parent?.id || null;
};

/**
 * Helper: Recursively find parent of a container
 */
const findParentRecursive = (
  parent: ContainerAPI,
  targetId: string
): ContainerAPI | null => {
  const nested = parent.content?.nestedContainers || [];

  // Check if any direct child is the target
  if (nested.some((c) => c.id === targetId)) {
    return parent;
  }

  // Check nested children recursively
  for (const child of nested) {
    const found = findParentRecursive(child, targetId);
    if (found) {
      return found;
    }
  }

  return null;
};

// ============================================================================
// Container Lookup Functions
// ============================================================================

/**
 * Finds a container by ID anywhere in the hierarchy
 *
 * @param containers - Top-level containers in the canvas
 * @param containerId - ID of the container to find
 * @returns The container or null if not found
 *
 * Time: O(n), Space: O(d)
 */
export const findContainerById = (
  containers: ContainerAPI[],
  containerId: string
): ContainerAPI | null => {
  // Check top-level
  const topLevel = containers.find((c) => c.id === containerId);
  if (topLevel) {
    return topLevel;
  }

  // Search nested
  for (const container of containers) {
    const found = findContainerRecursive(container, containerId);
    if (found) {
      return found;
    }
  }

  return null;
};

/**
 * Helper: Recursively find a container by ID
 */
const findContainerRecursive = (
  parent: ContainerAPI,
  targetId: string
): ContainerAPI | null => {
  const nested = parent.content?.nestedContainers || [];

  for (const child of nested) {
    if (child.id === targetId) {
      return child;
    }

    const found = findContainerRecursive(child, targetId);
    if (found) {
      return found;
    }
  }

  return null;
};

/**
 * Gets all descendants of a container (recursive)
 *
 * @param container - Container to get descendants from
 * @returns Array of all descendant containers
 *
 * Time: O(n), Space: O(n)
 */
export const getDescendants = (container: ContainerAPI): ContainerAPI[] => {
  const descendants: ContainerAPI[] = [];
  const nested = container.content?.nestedContainers || [];

  for (const child of nested) {
    descendants.push(child);
    descendants.push(...getDescendants(child));
  }

  return descendants;
};

/**
 * Gets all containers flattened (including nested)
 *
 * @param containers - Top-level containers
 * @returns All containers flattened into single array
 *
 * Time: O(n), Space: O(n)
 */
export const flattenContainers = (containers: ContainerAPI[]): ContainerAPI[] => {
  const result: ContainerAPI[] = [];

  for (const container of containers) {
    result.push(container);
    result.push(...getDescendants(container));
  }

  return result;
};

// ============================================================================
// Ancestry & Relationship Functions
// ============================================================================

/**
 * Checks if one container is an ancestor of another
 *
 * @param containers - Top-level containers in the canvas
 * @param ancestorId - Potential ancestor container ID
 * @param descendantId - Potential descendant container ID
 * @returns Whether ancestorId is an ancestor of descendantId
 *
 * Time: O(n), Space: O(d)
 */
export const isAncestorOf = (
  containers: ContainerAPI[],
  ancestorId: string,
  descendantId: string
): boolean => {
  const ancestor = findContainerById(containers, ancestorId);
  if (!ancestor) return false;

  const descendants = getDescendants(ancestor);
  return descendants.some((d) => d.id === descendantId);
};

/**
 * Gets the path from root to a container
 *
 * @param containers - Top-level containers in the canvas
 * @param containerId - Target container ID
 * @returns Array of container IDs from root to target (inclusive)
 *
 * Time: O(n), Space: O(d)
 */
export const getContainerPath = (
  containers: ContainerAPI[],
  containerId: string
): string[] => {
  // If it's a top-level container, path is just itself
  const topLevel = containers.find((c) => c.id === containerId);
  if (topLevel) {
    return [containerId];
  }

  // Build path recursively
  for (const container of containers) {
    const path = findPathRecursive(container, containerId, [container.id]);
    if (path) {
      return path;
    }
  }

  return []; // Not found
};

/**
 * Helper: Recursively build path to container
 */
const findPathRecursive = (
  parent: ContainerAPI,
  targetId: string,
  currentPath: string[]
): string[] | null => {
  const nested = parent.content?.nestedContainers || [];

  for (const child of nested) {
    if (child.id === targetId) {
      return [...currentPath, child.id];
    }

    const path = findPathRecursive(child, targetId, [...currentPath, child.id]);
    if (path) {
      return path;
    }
  }

  return null;
};

/**
 * Gets the root container for any container (traverses up to top-level)
 *
 * @param containers - Top-level containers in the canvas
 * @param containerId - Container to find root for
 * @returns Root container or the container itself if already top-level
 *
 * Time: O(n), Space: O(d)
 */
export const getRootContainer = (
  containers: ContainerAPI[],
  containerId: string
): ContainerAPI | null => {
  // If it's a top-level container, return it
  const topLevel = containers.find((c) => c.id === containerId);
  if (topLevel) {
    return topLevel;
  }

  // Find which top-level container contains this one
  for (const container of containers) {
    const descendants = getDescendants(container);
    if (descendants.some((d) => d.id === containerId)) {
      return container;
    }
  }

  return null;
};

// ============================================================================
// Sibling Functions
// ============================================================================

/**
 * Gets sibling containers (containers at the same level with same parent)
 *
 * @param containers - Top-level containers in the canvas
 * @param containerId - Container to find siblings for
 * @param includeSelf - Whether to include the container itself (default: false)
 * @returns Array of sibling containers
 *
 * Time: O(n), Space: O(k) where k = number of siblings
 */
export const getSiblings = (
  containers: ContainerAPI[],
  containerId: string,
  includeSelf: boolean = false
): ContainerAPI[] => {
  // Check if top-level container
  const isTopLevel = containers.some((c) => c.id === containerId);
  if (isTopLevel) {
    return includeSelf
      ? containers
      : containers.filter((c) => c.id !== containerId);
  }

  // Find parent and return its children
  const parent = findParentContainer(containers, containerId);
  if (!parent) {
    return [];
  }

  const siblings = parent.content?.nestedContainers || [];
  return includeSelf
    ? siblings
    : siblings.filter((c) => c.id !== containerId);
};
